import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { IDS } from '@/constants/testIds';
import { SPONSOR } from '@/constants/sponsor';
import {
  Loader2, PhoneCall, Search, TrendingUp, Zap, ArrowRight, ShieldAlert, RefreshCw,
} from 'lucide-react';

export default function SponsorAdminPage() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/sponsor/stats');
        setStats(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, refreshTick]);

  if (authLoading || loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-24 flex justify-center">
        <Loader2 size={32} className="animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24">
        <div data-testid={IDS.adminForbidden} className="industrial-card border-t-4 border-t-red-500 text-center py-16">
          <ShieldAlert size={40} className="text-red-500 mx-auto mb-4" />
          <h1 className="font-display text-3xl text-zinc-100 tracking-tight mb-2">ACCESS DENIED</h1>
          <p className="text-zinc-400 mb-2">{error}</p>
          <p className="text-zinc-500 text-sm font-mono">
            Only the sponsor admin can view this page. Signed in as{' '}
            <span className="text-amber-400">{user?.email}</span>.
          </p>
          <Link to="/app" className="btn-primary mt-6 inline-flex">Back to app</Link>
        </div>
      </div>
    );
  }

  const t = stats.totals;
  const maxDaily = Math.max(1, ...stats.daily_14d.map((d) => d.count));

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4 fade-up">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-2">
            /sponsor_dashboard
          </div>
          <h1 className="font-display text-5xl sm:text-6xl text-zinc-100 tracking-tight leading-none">
            REFERRAL <span className="text-gradient-hot">STATS</span>
          </h1>
          <p className="text-zinc-400 mt-3 flex items-center gap-2">
            <Zap size={16} className="text-amber-400" />
            {stats.sponsor.name} · {stats.sponsor.city} · <span className="font-mono">{stats.sponsor.phone}</span>
          </p>
        </div>
        <button
          onClick={() => setRefreshTick((n) => n + 1)}
          className="btn-secondary text-sm"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Big Stat Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <BigStat testid={IDS.adminTotalCalls} icon={PhoneCall} label="Total Calls" value={t.calls_all_time} accent="amber" />
        <BigStat testid={IDS.adminCalls7d} icon={TrendingUp} label="Last 7 Days" value={t.calls_7d} accent="lime" />
        <BigStat testid={IDS.adminCalls30d} icon={TrendingUp} label="Last 30 Days" value={t.calls_30d} accent="sky" />
        <BigStat testid={IDS.adminConversion} icon={ArrowRight} label="Call-vs-Search %" value={`${t.conversion_rate_pct}%`} accent="amber" sub={`${t.calls_all_time} calls · ${t.searches_all_time} searches`} />
      </div>

      {/* Daily chart */}
      <div data-testid={IDS.adminDailyChart} className="industrial-card border-t-4 border-t-amber-400 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-xs uppercase tracking-widest text-amber-400">
            Calls · Last 14 Days
          </div>
          <div className="font-mono text-xs text-zinc-500">peak: {maxDaily}</div>
        </div>
        <div className="flex items-end gap-1 sm:gap-2 h-40">
          {stats.daily_14d.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
              <div className="w-full flex items-end h-full">
                <div
                  className="w-full bg-gradient-to-t from-amber-500 to-amber-300 hover:from-lime-500 hover:to-lime-300 transition-colors"
                  style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count > 0 ? 4 : 0 }}
                  title={`${d.date}: ${d.count} calls`}
                />
              </div>
              <div className="font-mono text-[9px] text-zinc-500 uppercase">
                {d.date.slice(5)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {/* By Placement */}
        <div data-testid={IDS.adminByPlacement} className="industrial-card border-t-2 border-t-lime-400">
          <div className="font-mono text-xs uppercase tracking-widest text-lime-400 mb-4">
            Calls By Placement
          </div>
          {stats.by_placement.length === 0 ? (
            <div className="text-zinc-500 text-sm italic">No calls yet.</div>
          ) : (
            <div className="space-y-2">
              {stats.by_placement.map((row) => {
                const pct = t.calls_all_time > 0 ? (row.count / t.calls_all_time) * 100 : 0;
                return (
                  <div key={row.placement}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-mono text-zinc-300">{prettyPlacement(row.placement)}</span>
                      <span className="font-mono text-lime-400">{row.count}</span>
                    </div>
                    <div className="h-1.5 bg-zinc-800 relative">
                      <div className="absolute inset-y-0 left-0 bg-lime-400" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* By Trade */}
        <div data-testid={IDS.adminByTrade} className="industrial-card border-t-2 border-t-sky-400">
          <div className="font-mono text-xs uppercase tracking-widest text-sky-400 mb-4">
            Calls By Trade Context
          </div>
          {stats.by_trade.length === 0 ? (
            <div className="text-zinc-500 text-sm italic">No calls yet.</div>
          ) : (
            <div className="space-y-2">
              {stats.by_trade.map((row) => {
                const pct = t.calls_all_time > 0 ? (row.count / t.calls_all_time) * 100 : 0;
                return (
                  <div key={row.trade || 'unknown'}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-mono text-zinc-300">{row.trade || 'General (no trade)'}</span>
                      <span className="font-mono text-sky-400">{row.count}</span>
                    </div>
                    <div className="h-1.5 bg-zinc-800 relative">
                      <div className="absolute inset-y-0 left-0 bg-sky-400" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="industrial-card">
        <div className="font-mono text-xs uppercase tracking-widest text-amber-400 mb-4 flex items-center gap-2">
          <Search size={12} /> Recent Activity (Last 20)
        </div>
        {stats.recent.length === 0 ? (
          <div className="text-zinc-500 text-sm italic">
            No calls tracked yet. Once users tap a &quot;Call Contact Electric&quot; button anywhere in the app, it shows up here.
          </div>
        ) : (
          <table data-testid={IDS.adminRecentTable} className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="py-2 pr-2">When</th>
                <th className="py-2 pr-2">Placement</th>
                <th className="py-2 pr-2">Trade</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent.map((row) => (
                <tr key={row.id} className="border-b border-zinc-900/60 hover:bg-zinc-900/50 transition-colors">
                  <td className="py-2 pr-2 font-mono text-xs text-zinc-400">{fmtDate(row._ts)}</td>
                  <td className="py-2 pr-2">
                    <span className="inline-block font-mono text-[10px] uppercase tracking-widest bg-lime-500/10 text-lime-400 border border-lime-500/30 px-2 py-0.5">
                      {prettyPlacement(row.placement)}
                    </span>
                  </td>
                  <td className="py-2 pr-2 font-mono text-xs text-zinc-300">{row.trade || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BigStat({ testid, icon: Icon, label, value, accent = 'amber', sub }) {
  const accents = {
    amber: 'border-t-amber-400 text-amber-400',
    lime: 'border-t-lime-400 text-lime-400',
    sky: 'border-t-sky-400 text-sky-400',
  };
  const a = accents[accent] || accents.amber;
  return (
    <div data-testid={testid} className={`industrial-card border-t-4 ${a} hover:-translate-y-1 transition-transform`}>
      <Icon size={20} className={a.split(' ')[1]} strokeWidth={2} />
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-3">{label}</div>
      <div className="font-display text-4xl text-zinc-100 tracking-tight mt-1">{value}</div>
      {sub && <div className="text-zinc-500 text-xs mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function prettyPlacement(p) {
  if (!p) return 'Unknown';
  const map = {
    banner: 'Top Marquee Banner',
    footer: 'Footer',
    landing_spotlight: 'Landing Page Spotlight',
    solution_card: 'Solution Page Card',
    modal_featured: 'Call-Pro Modal Card',
    advanced_badge: 'Advanced Warning Banner',
  };
  return map[p] || p;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
